import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
	PRODUCT_REVIEWS_SECTION_ID,
	ProductReviews,
	useProductCurrentReviewSummary,
	useProductReviewsTabData
} from '@/features/product-reviews'
import { ProductServices } from '@/features/product-services'
import { Pickup } from '@/features/product/choose-pickup-point'

import { OPEN_PRODUCT_REVIEWS_EVENT } from '@/entities/card/ui/product-rating/ProductRating'

import { Tabs } from '@/shared/ui'

import { useProductFlags } from '../../../hooks/use-product-flags'
import { useProductPageData } from '../../../hooks/use-product-page-data'
import { useProduct } from '../../../hooks/useProduct'

import { AccessoriesTab } from './accessories-tab/accessories-tab'
import { CreditTab, OPEN_PRODUCT_CREDIT_TAB_EVENT } from './credit-tab/credit-tab'
import { DescriptionTab } from './description-tab/description-tab'
import { DiscountTab } from './discount-tab/discount-tab'
import { MarkdownTab } from './markdown-tab/markdown-tab'
import { ProductRecommendationsBlock } from './product-recommendations-block/product-recommendations-block'
import { TechSpecsTab } from './tech-specs-tab/tech-specs-tab'

export const TABS_ID_ATTR = `productTabs`

const VALID_TAB_KEYS = [
	`description`,
	`specs`,
	`map`,
	`accessories`,
	`services`,
	`reviews`,
	`credit`,
	`discount`,
	`markdown`
]

const REVIEWS_TAB_KEY = `reviews`
const CREDIT_TAB_KEY = `credit`
const SCROLL_GAP = 16

const getStickyTabsOffset = () => {
	const tabsElement = document.getElementById(TABS_ID_ATTR)
	const tabsNavElement = tabsElement?.querySelector<HTMLElement>('[class*="tabsNav"]')

	if (!tabsNavElement) return window.innerWidth <= 848 ? 70 : 160

	const { top } = window.getComputedStyle(tabsNavElement)
	const stickyTop = Number.parseFloat(top) || 0

	return stickyTop + tabsNavElement.offsetHeight + SCROLL_GAP
}

export const ProductTabs = () => {
	const searchParams = useSearchParams()
	const { product, crossLinks, isCrossLinksLoading } = useProduct()

	const { isProductBuy, isShowDiscount, isShowMarkdown, isInStock, productIsInStock } = useProductFlags()
	const { description, services, payments } = useProductPageData(product.ID)

	const tabFromUrl = searchParams?.get(`tab`) ?? null
	const desiredTab = tabFromUrl && VALID_TAB_KEYS.includes(tabFromUrl) ? tabFromUrl : null

	const [activeTab, setActiveTab] = useState(desiredTab || `specs`)
	const hasScrolledRef = useRef(false)
	const showAllAccessories = isProductBuy && product.HAS_ACCESSORIES
	const { summary: reviewsSummary } = useProductCurrentReviewSummary(product, crossLinks, isCrossLinksLoading)
	const reviews = useProductReviewsTabData(product, crossLinks, activeTab === REVIEWS_TAB_KEY, reviewsSummary)
	const reviewsCount = reviewsSummary?.COUNT ?? 0
	const reviewsTabTitle = reviewsCount > 0 ? `Отзывы (${reviewsCount})` : `Отзывы`

	const scrollToElement = useCallback((elementId: string) => {
		const targetEl = document.getElementById(elementId)
		if (!targetEl) return

		const elementTop = targetEl.getBoundingClientRect().top + window.scrollY
		const offset = getStickyTabsOffset()
		window.scrollTo({ top: elementTop - offset, behavior: 'smooth' })
	}, [])

	const scrollToSection = useCallback(
		(elementId: string) => {
			window.requestAnimationFrame(() => {
				window.setTimeout(() => scrollToElement(elementId), 0)
			})
		},
		[scrollToElement]
	)

	const handleShowAllAccessoriesClick = useCallback(() => {
		scrollToElement(TABS_ID_ATTR)
		setTimeout(() => setActiveTab('accessories'), 300)
	}, [scrollToElement])

	const tabItems = useMemo(
		() => [
			{
				tab: `Описание`,
				content: <DescriptionTab description={description} productKod={product.KOD} />,
				key: `description`,
				hidden: !description,
				forceRender: true
			},
			{
				tab: `Характеристики`,
				content: <TechSpecsTab productTitle={product.TITLE} items={product.PROPERTIES} />,
				key: `specs`,
				forceRender: true
			},
			{
				tab: reviewsTabTitle,
				content: (
					<ProductReviews
						productCode={Number(product.KOD)}
						productCodes={reviews.productCodes}
						productTitle={product.TITLE}
						productImages={reviews.productImages}
						token={reviews.token}
						initialData={reviews.initialData}
						isLoading={reviews.isLoading}
						hasOwnCurrentVariantReview={reviews.hasOwnCurrentVariantReview}
					/>
				),
				key: `reviews`,
				forceRender: true
			},
			{
				tab: `Наличие в магазинах`,
				content: <Pickup inProduct productId={product.ID} />,
				key: `map`,
				hidden: !isInStock || !isProductBuy || !productIsInStock
			},
			{
				tab: `Аксессуары`,
				content: <AccessoriesTab productId={product.ID} />,
				key: `accessories`,
				hidden: !isProductBuy || !product.HAS_ACCESSORIES
			},
			{
				tab: `Услуги`,
				content: <ProductServices viewType="list" services={services} relatedId={product.ID} />,
				key: `services`,
				hidden: !isProductBuy || !product.HAS_SERVICES
			},
			{
				tab: `Рассрочка и кредиты`,
				content: (
					<CreditTab
						installmentPrograms={null}
						price={product.PRICE}
						productKod={product.KOD}
						productId={product.ID}
						creditData={payments.creditData}
						installmentData={payments.installmentData}
					/>
				),
				key: `credit`,
				forceRender: true,
				hidden: !isInStock || !isProductBuy || !productIsInStock || (!payments.creditData && !payments.installmentData)
			},
			{
				tab: `Товары из обмена в Трейд-ин`,
				content: <DiscountTab KOD={product.KOD} />,
				key: `discount`,
				hidden: !isShowDiscount
			},
			{
				tab: `Уценка`,
				content: <MarkdownTab KOD={product.KOD} />,
				key: `markdown`,
				hidden: !isShowMarkdown
			}
		],
		[
			description,
			services,
			payments,
			reviews,
			reviewsTabTitle,
			product.ID,
			product.KOD,
			product.TITLE,
			product.PROPERTIES,
			product.HAS_ACCESSORIES,
			product.HAS_SERVICES,
			product.PRICE,
			isProductBuy,
			isShowDiscount,
			isShowMarkdown
		]
	)

	// Активируем таб из URL когда он станет доступен и скроллим к табам
	useEffect(() => {
		if (!desiredTab || hasScrolledRef.current) return

		const targetTab = tabItems.find(item => item.key === desiredTab && !item.hidden)
		if (targetTab) {
			setActiveTab(desiredTab)

			// Даём время на рендер и скроллим к табам
			const timeoutId = setTimeout(() => {
				if (!hasScrolledRef.current) {
					hasScrolledRef.current = true
					scrollToElement(desiredTab === REVIEWS_TAB_KEY ? PRODUCT_REVIEWS_SECTION_ID : TABS_ID_ATTR)
				}
			}, 300)

			return () => clearTimeout(timeoutId)
		}
	}, [desiredTab, scrollToElement, tabItems])

	useEffect(() => {
		const openTabAndScroll = (tabKey: string, sectionId: string) => {
			const targetTab = tabItems.find(item => item.key === tabKey && !item.hidden)
			if (!targetTab) return

			setActiveTab(tabKey)
			scrollToSection(sectionId)
		}

		const openReviews = () => openTabAndScroll(REVIEWS_TAB_KEY, PRODUCT_REVIEWS_SECTION_ID)
		const openCreditTab = (event: Event) => {
			const sectionId = (event as CustomEvent<string>).detail
			if (!sectionId) return

			openTabAndScroll(CREDIT_TAB_KEY, sectionId)
		}

		window.addEventListener(OPEN_PRODUCT_REVIEWS_EVENT, openReviews)
		window.addEventListener(OPEN_PRODUCT_CREDIT_TAB_EVENT, openCreditTab)

		return () => {
			window.removeEventListener(OPEN_PRODUCT_REVIEWS_EVENT, openReviews)
			window.removeEventListener(OPEN_PRODUCT_CREDIT_TAB_EVENT, openCreditTab)
		}
	}, [scrollToSection, tabItems])

	return (
		<>
			<Tabs
				id={TABS_ID_ATTR}
				defaultActiveKey={'specs'}
				activeKey={activeTab}
				onChange={setActiveTab}
				items={tabItems}
			/>
			<ProductRecommendationsBlock
				activeTab={activeTab}
				showAllAccessories={showAllAccessories}
				onShowAllAccessories={handleShowAllAccessoriesClick}
			/>
		</>
	)
}
